import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, 
  TrendingUp, 
  Shield, 
  Clock, 
  Globe,
  FileText,
  Building2,
  Truck,
  Users,
  CheckCircle2,
  ChevronRight,
  Zap,
  BarChart3,
  Wallet
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const images = {
  logistics: [
    "/images/home/logistics-1.jpg",
    "/images/home/logistics-2.jpg",
    "/images/home/logistics-3.jpg"
  ],
  corporate: [
    "/images/home/corporate-1.jpg"
  ]
};

const Header = () => {
  const { t, language, changeLanguage, languages } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-emerald-950/95 backdrop-blur-md border-b border-emerald-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo">
            <div className="w-8 h-8 bg-lime-400 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-950" />
            </div>
            <span className="text-xl font-bold text-white font-serif">TradeFlow</span>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#demo-transaction" className="text-slate-300 hover:text-lime-400 transition-colors text-sm font-medium">{t('home.nav.demo')}</a>
            <a href="#platform-roles" className="text-slate-300 hover:text-lime-400 transition-colors text-sm font-medium">{t('auth_role')}</a>
            <a href="#scoring" className="text-slate-300 hover:text-lime-400 transition-colors text-sm font-medium">{t('nav_services')}</a>
            <a href="#stories" className="text-slate-300 hover:text-lime-400 transition-colors text-sm font-medium">{t('nav_blog')}</a>
            <a href="#how-it-works" className="text-slate-300 hover:text-lime-400 transition-colors text-sm font-medium">{t('nav_about')}</a>
          </nav>
          
          <div className="flex items-center space-x-4">
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-16 bg-transparent border-emerald-800 text-white" data-testid="language-switcher">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((item) => (
                  <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  className="text-slate-300 hover:text-white"
                  onClick={() => navigate('/dashboard')}
                  data-testid="dashboard-btn"
                >
                  {t('nav_dashboard')}
                </Button>
                <Button 
                  className="bg-lime-400 text-emerald-950 hover:bg-lime-500"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  {t('nav_logout')}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  className="text-slate-300 hover:text-white"
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  {t('nav_login')}
                </Button>
                <Button 
                  className="bg-lime-400 text-emerald-950 hover:bg-lime-500"
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                >
                  {t('nav_register')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative min-h-screen bg-emerald-950 pt-16 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/50 via-emerald-950 to-emerald-950"></div>
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
        <div className="absolute inset-0 bg-gradient-to-l from-lime-400/20 to-transparent"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-fade-in">
            <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/30 px-4 py-1">
              {t('home.hero.badge')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-white leading-tight tracking-tight" data-testid="hero-title">
              {t('hero_title')}
            </h1>
            <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-lime-400 text-emerald-950 hover:bg-lime-500 rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-lime-400/20"
                onClick={() => document.getElementById('demo-transaction')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta"
              >
                {t('hero_cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-base"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-secondary"
              >
                {t('hero_secondary')}
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-emerald-800">
              {[
                ['home.hero.statSpeedValue', 'home.hero.statSpeedLabel'],
                ['home.hero.statAdvanceValue', 'home.hero.statAdvanceLabel'],
                ['home.hero.statPartnersValue', 'home.hero.statPartnersLabel']
              ].map(([valueKey, labelKey]) => (
                <div key={valueKey} className="space-y-2">
                  <p className="text-3xl font-bold text-lime-400 font-mono">{t(valueKey)}</p>
                  <p className="text-sm text-slate-500">{t(labelKey)}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative animate-slide-in-right">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.corporate[0]} 
                alt="Finance visualization" 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-transparent to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl animate-scale-in animate-delay-300">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t('home.hero.approvedTitle')}</p>
                  <p className="text-xs text-slate-500">{t('home.hero.approvedMeta')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const demoTransaction = {
  seller: 'Demo Machinery Co.',
  buyer: 'Demo Distribution LLP',
  corridor: 'China -> Kazakhstan',
  invoice: '$200,000',
  tenor: '60 days',
  advanceRate: '80%',
  financing: '$160,000',
  annualizedRate: '18%',
  platformFee: '1.0%',
  riskGrade: 'B+',
  status: null
};

const DemoTransactionSection = () => {
  const { t } = useLanguage();
  const demo = t('home.demo');
  const stepIcons = [FileText, CheckCircle2, Shield, Wallet, Clock];
  const steps = demo.steps.map((step, index) => ({ ...step, icon: stepIcons[index] }));
  const roleViews = Object.values(demo.roles);

  return (
    <section id="demo-transaction" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 mb-4">
            {demo.badge}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4">
            {demo.title}
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            {demo.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 mb-10">
          <Card className="bg-slate-950 text-white border-slate-800 rounded-xl">
            <CardHeader>
              <CardTitle className="font-sans text-xl">{demo.caseTitle}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">{demo.sellerLabel}</p>
                  <p className="font-semibold">{demoTransaction.seller}</p>
                </div>
                <div>
                  <p className="text-slate-400">{demo.buyerLabel}</p>
                  <p className="font-semibold">{demoTransaction.buyer}</p>
                </div>
                <div>
                  <p className="text-slate-400">{demo.corridorLabel}</p>
                  <p className="font-semibold">{demoTransaction.corridor}</p>
                </div>
                <div>
                  <p className="text-slate-400">{demo.statusLabel}</p>
                  <p className="font-semibold text-lime-300">{t('home.demo.statusValue')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-5 border-t border-slate-800">
                {[
                  [demo.metrics.invoice, demoTransaction.invoice],
                  [demo.metrics.term, demoTransaction.tenor],
                  [demo.metrics.advance, demoTransaction.advanceRate],
                  [demo.metrics.financing, demoTransaction.financing],
                  [demo.metrics.pricing, demoTransaction.annualizedRate],
                  [demo.metrics.fee, demoTransaction.platformFee]
                ].map(([label, value]) => (
                  <div key={label} className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="text-lg font-mono font-semibold">{value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            {roleViews.map((view) => (
              <Card key={view.title} className="bg-slate-50 border-slate-200 rounded-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="font-sans text-base">{view.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {view.items.map((item) => (
                      <div key={item} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          {steps.map((step, index) => (
            <div key={step.title} className="relative bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-10 h-10 bg-emerald-950 rounded-lg flex items-center justify-center mb-4">
                <step.icon className="w-5 h-5 text-lime-400" />
              </div>
              <p className="text-xs font-mono text-emerald-700 mb-2">0{index + 1}</p>
              <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{step.detail}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-emerald-950 mb-1">{demo.lenderTitle}</h3>
              <p className="text-sm text-emerald-800">{demo.lenderText}</p>
            </div>
            <Button
              className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-6"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {demo.cta}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const PlatformRolesSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const roleIcons = [FileText, CheckCircle2, Users, Wallet, Building2];
  const roles = t('home.roles.items').map((role, index) => ({ ...role, icon: roleIcons[index] }));

  return (
    <section id="platform-roles" className="py-24 bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
          <div className="lg:sticky lg:top-24">
            <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/30 mb-4">
              {t('home.roles.badge')}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-serif font-medium mb-4">
              {t('home.roles.title')}
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              {t('home.roles.subtitle')}
            </p>
            <Button
              className="bg-lime-400 text-emerald-950 hover:bg-lime-500 rounded-full px-6"
              onClick={() => navigate('/register')}
            >
              {t('home.roles.cta')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {roles.map((role, index) => (
              <Card key={role.title} className={`rounded-2xl border-slate-800 bg-slate-900/70 text-white ${index === roles.length - 1 ? 'md:col-span-2' : ''}`}>
                <CardContent className="p-6">
                  <div className="w-11 h-11 bg-lime-400/10 rounded-xl flex items-center justify-center mb-5">
                    <role.icon className="w-5 h-5 text-lime-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{role.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ScoringSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    annual_revenue: '',
    years_in_business: '',
    industry: '',
    country: 'kazakhstan',
    outstanding_invoices: '',
    has_financial_statements: false,
    has_bank_statements: false
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const industries = [
    { value: 'manufacturing', label: t('industry_manufacturing') },
    { value: 'trade', label: t('industry_trade') },
    { value: 'services', label: t('industry_services') },
    { value: 'logistics', label: t('industry_logistics') },
    { value: 'agriculture', label: t('industry_agriculture') },
    { value: 'it', label: t('industry_it') },
    { value: 'construction', label: t('industry_construction') },
    { value: 'other', label: t('industry_other') }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/scoring/test`, {
        ...formData,
        annual_revenue: parseFloat(formData.annual_revenue) || 0,
        years_in_business: parseInt(formData.years_in_business) || 0,
        outstanding_invoices: parseFloat(formData.outstanding_invoices) || 0
      });
      setResult(response.data);
    } catch (error) {
      console.error('Scoring error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-emerald-500';
      case 'moderate': return 'text-amber-500';
      case 'elevated': return 'text-orange-500';
      case 'high': return 'text-rose-500';
      default: return 'text-slate-500';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <section id="scoring" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4">
            <Zap className="w-3 h-3 mr-1" />
            {t('scoring_title')}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4" data-testid="scoring-title">
            {t('scoring_subtitle')}
          </h2>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          <Card className="bg-white rounded-2xl shadow-sm border border-slate-100" data-testid="scoring-form-card">
            <CardHeader>
              <CardTitle className="font-sans text-xl">{t('scoring_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label>{t('scoring_company')}</Label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    placeholder={t('home.scoring.companyPlaceholder')}
                    className="bg-slate-50 border-slate-200"
                    data-testid="scoring-company-input"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('scoring_revenue')}</Label>
                    <Input 
                      type="number"
                      value={formData.annual_revenue}
                      onChange={(e) => setFormData({...formData, annual_revenue: e.target.value})}
                      placeholder={t('home.scoring.revenuePlaceholder')}
                      className="bg-slate-50 border-slate-200"
                      data-testid="scoring-revenue-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('scoring_years')}</Label>
                    <Input 
                      type="number"
                      value={formData.years_in_business}
                      onChange={(e) => setFormData({...formData, years_in_business: e.target.value})}
                      placeholder="5"
                      className="bg-slate-50 border-slate-200"
                      data-testid="scoring-years-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('scoring_industry')}</Label>
                    <Select value={formData.industry} onValueChange={(v) => setFormData({...formData, industry: v})}>
                      <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="scoring-industry-select">
                        <SelectValue placeholder={t('home.scoring.industryPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map(i => (
                          <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('scoring_invoices')}</Label>
                    <Input 
                      type="number"
                      value={formData.outstanding_invoices}
                      onChange={(e) => setFormData({...formData, outstanding_invoices: e.target.value})}
                      placeholder={t('home.scoring.invoicesPlaceholder')}
                      className="bg-slate-50 border-slate-200"
                      data-testid="scoring-invoices-input"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="financial" 
                      checked={formData.has_financial_statements}
                      onCheckedChange={(c) => setFormData({...formData, has_financial_statements: c})}
                      data-testid="scoring-financial-checkbox"
                    />
                    <label htmlFor="financial" className="text-sm text-slate-600">{t('scoring_financial')}</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="bank" 
                      checked={formData.has_bank_statements}
                      onCheckedChange={(c) => setFormData({...formData, has_bank_statements: c})}
                      data-testid="scoring-bank-checkbox"
                    />
                    <label htmlFor="bank" className="text-sm text-slate-600">{t('scoring_bank')}</label>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-950 hover:bg-emerald-900 text-white rounded-full py-6"
                  disabled={loading}
                  data-testid="scoring-submit-btn"
                >
                  {loading ? t('msg_loading') : t('scoring_submit')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
          
          <div className="space-y-6">
            {result ? (
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-scale-in" data-testid="scoring-result-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-sans text-xl">{t('scoring_result_title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm text-slate-500 mb-1">{t('scoring_score')}</p>
                      <p className="text-4xl font-bold font-mono text-emerald-600" data-testid="scoring-result-score">{result.score}</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm text-slate-500 mb-1">{t('scoring_risk')}</p>
                      <p className={`text-2xl font-semibold ${getRiskColor(result.risk_level)}`} data-testid="scoring-result-risk">
                        {t(`risk_${result.risk_level}`)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-emerald-50 rounded-xl mb-6">
                    <p className="text-sm text-emerald-700 mb-1">{t('scoring_limit')}</p>
                    <p className="text-3xl font-bold text-emerald-700 font-mono" data-testid="scoring-result-limit">
                      {formatCurrency(result.preliminary_limit)}
                    </p>
                  </div>
                  
                  {result.recommendations?.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-700">{t('scoring_recommendations')}:</p>
                      {result.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start space-x-2 text-sm text-slate-600">
                          <ChevronRight className="w-4 h-4 text-lime-500 flex-shrink-0 mt-0.5" />
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    className="w-full mt-6 bg-lime-400 text-emerald-950 hover:bg-lime-500 rounded-full"
                    onClick={() => navigate('/register')}
                    data-testid="scoring-register-btn"
                  >
                    {t('home.scoring.fullAssessment')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="h-full flex items-center justify-center p-8 bg-white rounded-2xl border border-slate-100 border-dashed">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t('home.scoring.empty')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const stepIcons = [FileText, BarChart3, Users, Wallet];
  const steps = t('home.how.steps').map((step, index) => ({ ...step, icon: stepIcons[index] }));

  return (
    <section id="how-it-works" className="py-24 bg-emerald-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/30 mb-4">
            {t('section_how_it_works')}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-white mb-4">
            {t('home.how.title')}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={step.title} className="relative group">
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-lime-400/30 transition-all h-full">
                <div className="w-12 h-12 bg-lime-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-lime-400/20 transition-colors">
                  <step.icon className="w-6 h-6 text-lime-400" />
                </div>
                <div className="text-sm font-mono text-lime-400 mb-2">0{index + 1}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.description}</p>
              </div>
              {index < 3 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-slate-800 -translate-y-1/2"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const StoriesSection = () => {
  const { t } = useLanguage();
  const stories = t('home.stories.cards').map((story, index) => ({
    ...story,
    image: images.logistics[index % images.logistics.length]
  }));

  return (
    <section id="stories" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4">
            {t('section_stories')}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4">
            {t('home.stories.title')}
          </h2>
        </div>
        
        <Carousel className="w-full" data-testid="stories-carousel">
          <CarouselContent>
            {stories.map((story) => (
              <CarouselItem key={story.author} className="md:basis-1/2 lg:basis-1/3 pl-4">
                <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 h-full">
                  <CardContent className="p-6">
                    <div className="h-48 rounded-xl overflow-hidden mb-6">
                      <img
                        src={story.image}
                        alt={story.company}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <blockquote className="text-slate-700 italic mb-6 font-serif">
                      {story.quote}
                    </blockquote>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold">{story.author[0]}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{story.author}</p>
                        <p className="text-sm text-slate-500">{story.role}, {story.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-4" />
          <CarouselNext className="-right-4" />
        </Carousel>
      </div>
    </section>
  );
};

const OffersSection = () => {
  const { t } = useLanguage();
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await axios.get(`${API}/featured-offers`);
        setOffers(response.data);
      } catch (error) {
        console.error('Failed to fetch offers:', error);
      }
    };
    fetchOffers();
  }, []);

  const defaultOffers = t('home.offers.cards').map((offer, index) => ({
    id: String(index + 1),
    ...offer,
    image_url: images.logistics[index % images.logistics.length]
  }));

  const displayOffers = offers.length > 0 ? offers : defaultOffers;

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4">
            {t('section_offers')}
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-4">
            {t('home.offers.title')}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8" data-testid="offers-grid">
          {displayOffers.map((offer) => (
            <Card key={offer.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow overflow-hidden group">
              <div className="h-48 overflow-hidden">
                <img 
                  src={offer.image_url || images.logistics[0]} 
                  alt={offer.title} 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-6">
                <Badge variant="outline" className="mb-3 text-xs">
                  {offer.offer_type === 'logistics' && <Truck className="w-3 h-3 mr-1" />}
                  {offer.offer_type === 'product' && <Building2 className="w-3 h-3 mr-1" />}
                  {offer.offer_type}
                </Badge>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{offer.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{offer.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">{offer.company_name}</span>
                  <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
                    {t('home.offers.detail')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-emerald-950 py-16 border-t border-emerald-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-lime-400 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-950" />
              </div>
              <span className="text-xl font-bold text-white font-serif">TradeFlow</span>
            </div>
            <p className="text-slate-400 text-sm">
              {t('home.footer.tagline')}
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">{t('home.footer.companyHeading')}</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('footer_about')}</a></li>
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('footer_contact')}</a></li>
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('home.footer.careers')}</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">{t('home.footer.servicesHeading')}</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('home.footer.factoring')}</a></li>
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('home.footer.tradeFinance')}</a></li>
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('home.footer.bnpl')}</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">{t('home.footer.legalHeading')}</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('footer_terms')}</a></li>
              <li><a href="#" className="text-slate-400 hover:text-lime-400 text-sm transition-colors">{t('footer_privacy')}</a></li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-emerald-900 flex flex-col md:flex-row items-center justify-between">
          <p className="text-slate-500 text-sm">{t('footer_copyright')}</p>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <Globe className="w-5 h-5 text-slate-500" />
            <Shield className="w-5 h-5 text-slate-500" />
            <Clock className="w-5 h-5 text-slate-500" />
          </div>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header />
      <HeroSection />
      <DemoTransactionSection />
      <PlatformRolesSection />
      <ScoringSection />
      <HowItWorksSection />
      <StoriesSection />
      <OffersSection />
      <Footer />
    </div>
  );
};

export default HomePage;
